import './JobDetail.css'; 
import { ROLES } from '../config/routes';

// Updated JSX component to work with your backend data structure
const JobDetailsTab = ({ jobData, role }) => {
    // Helper function to get category color (you'll need to implement this based on your color scheme)
    const getCategoryColor = (color) => {
      // Return appropriate CSS class based on color
      return color || 'default-color';
    };
  
    // Transform backend data to match component structure
    const jobDetails = {
      title: jobData.title,
      description: jobData.description,
      
      // Map requirements to different sections based on requirement_type
      responsibilities: jobData.requirements
        .filter(req => req.requirement_type === 'responsibility')
        .map(req => req.description),
      
      whoYouAre: jobData.requirements
        .filter(req => ['education', 'experience', 'essential'].includes(req.requirement_type))
        .map(req => req.description),
      
      niceToHaves: jobData.requirements
        .filter(req => req.requirement_type === 'preferred')
        .map(req => req.description),
      
      // Map benefits to perks section
      perksAndBenefits: jobData.benefits
        .map(benefit => `${benefit.benefit_name}: ${benefit.benefit_description}`)
        .join('. '),
      
      aboutRole: {
        applied: jobData.application_counts || 0,
        capacity: 100, // You might want to add this to your backend data
        applyBefore: new Date(jobData.due_date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        jobPostedOn: new Date(jobData.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        jobType: `${jobData.job_type} • ${jobData.location_type} • ${jobData.experience_level}`,
        salary: `${Number(jobData.salary_lower_rate).toLocaleString()} - $${Number(jobData.salary_higher_rate).toLocaleString()} ${jobData.currency_code}`
      },
      
      categories: [
        {
          name: jobData.category_name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
          color: 'blue' // You might want to add color mapping to your backend
        }
      ],
      
      requiredSkills: jobData.skills?.map(skill => skill.name) || []
    };
  
    return (
      <div className="job-details-tab">
        <div className="job-details-header">
          <div className="job-icon">
            {jobData.company_name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h2>{jobDetails.title}</h2>
            <p className="company-name">{jobData.company_name}</p>
            <p className="location">{jobData.address.city}, {jobData.address.state}, {jobData.address.country}</p>
          </div>
          { role == ROLES.FUND_MANAGER &&
            <button className="edit-btn">
              <span className="edit-icon">✏️</span>
              Edit Job Details
            </button>
          }
        </div>
  
        <div className="job-details-content">
          <div className="main-content">
            <section className="description-section">
              <h3>Description</h3>
              <p>{jobDetails.description}</p>
            </section>
  
            {jobDetails.responsibilities.length > 0 && (
              <section className="responsibilities-section">
                <h3>Responsibilities</h3>
                <ul className="responsibilities-list">
                  {jobDetails.responsibilities.map((responsibility, index) => (
                    <li key={index} className="responsibility-item">
                      <span className="check-icon">✓</span>
                      {responsibility}
                    </li>
                  ))}
                </ul>
              </section>
            )}
  
            {jobDetails.whoYouAre.length > 0 && (
              <section className="who-you-are-section">
                <h3>Who You Are</h3>
                <ul className="who-you-are-list">
                  {jobDetails.whoYouAre.map((item, index) => (
                    <li key={index} className="who-you-are-item">
                      <span className="check-icon">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </section>
            )}
  
            {jobDetails.niceToHaves.length > 0 && (
              <section className="nice-to-haves-section">
                <h3>Nice-To-Haves</h3>
                <ul className="nice-to-haves-list">
                  {jobDetails.niceToHaves.map((item, index) => (
                    <li key={index} className="nice-to-have-item">
                      <span className="check-icon">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </section>
            )}
  
            <section className="perks-section">
              <h3>Perks & Benefits</h3>
              <div className="benefits-list">
                {jobData.benefits.map((benefit, index) => (
                  <div key={benefit.benefit_id} className="benefit-item">
                    <h4>{benefit.benefit_name}</h4>
                    <p>{benefit.benefit_description}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
  
          <div className="sidebar">
            <div className="about-role-section">
              <h3>About this role</h3>
              <div className="role-stats">
                <div className="stat-item">
                  <span className="stat-number">{jobDetails.aboutRole.applied} applied</span>
                  <span className="stat-description">of {jobDetails.aboutRole.capacity} capacity</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${(jobDetails.aboutRole.applied / jobDetails.aboutRole.capacity) * 100}%` }}
                  ></div>
                </div>
              </div>
  
              <div className="role-details">
                <div className="detail-item">
                  <span className="detail-label">Apply Before</span>
                  <span className="detail-value">{jobDetails.aboutRole.applyBefore}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Job Posted On</span>
                  <span className="detail-value">{jobDetails.aboutRole.jobPostedOn}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Job Type</span>
                  <span className="detail-value">{jobDetails.aboutRole.jobType}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Salary</span>
                  <span className="detail-value">{jobDetails.aboutRole.salary}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Location</span>
                  <span className="detail-value">{jobData.address.street_address}</span>
                </div>
              </div>
            </div>
  
            <div className="categories-section">
              <h3>Categories</h3>
              <div className="categories-list">
                {jobDetails.categories.map((category, index) => (
                  <span key={index} className={`category-tag ${getCategoryColor(category.color)}`}>
                    {category.name}
                  </span>
                ))}
              </div>
            </div>
  
            {jobDetails.requiredSkills.length > 0 && (
              <div className="required-skills-section">
                <h3>Required Skills</h3>
                <div className="skills-list">
                  {jobDetails.requiredSkills.map((skill, index) => (
                    <span key={index} className="skill-tag">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
  
            {jobData.images && jobData.images.length > 0 && (
              <div className="company-images-section">
                <h3>Company Images</h3>
                <div className="images-grid">
                  {jobData.images.map((image) => (
                    <img
                      key={image.image_id}
                      src={image.image_url}
                      alt={image.image_description}
                      className="company-image"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  export default JobDetailsTab;